import React, { useEffect } from 'react';
import { Form, Select, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Option } = Select;

/**
 * Reusable Migration Type Selector component
 * @param {boolean} isMigrateByVM - Whether migration is by VM
 * @param {boolean} isMigrateNetAppFiles - Whether migrating NetApp files
 * @param {string} migrationType - Current migration type
 * @param {Function} onMigrationTypeChange - Handler for migration type change
 * @param {Object} selectedAccount - Selected target cloud account object
 */
export const MigrationTypeSelector = ({
    isMigrateByVM,
    isMigrateNetAppFiles,
    migrationType,
    onMigrationTypeChange,
    selectedAccount,
}) => {
    // Get target cloud type for option enabling/disabling logic
    const targetCloudType = selectedAccount?.cloud_type;
    // Block mobility type option availability based on cloud type
    // Following Backbone logic from components.js line 6002
    const isNewCloudDiskEnabled = targetCloudType === 'aws';
    const isUnifiedEnabled = ['aws', 'rivermeadow_standalone'].includes(targetCloudType);
    const isNewDiskOnHostEnabled = ['aws', 'rivermeadow_standalone'].includes(targetCloudType);
    return (
        <>
            {/* Migration Type Selection */}
            {!isMigrateByVM && (
                <Form.Item
                    label={
                        <span>
                            Select Migration Type{' '}
                            <Tooltip title="Workload Migration allows workloads to be virtualized and migrated into any target cloud. Block Migration allows data to be migrated at a block level to any supported block devices. File Mobility allows data to be migrated at a file level to any supported file systems.">
                                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                            </Tooltip>
                        </span>
                    }
                    name="migrationType"
                    initialValue="full"
                >
                    <Select onChange={onMigrationTypeChange}>
                        <Option value="full" disabled={isMigrateNetAppFiles}>
                            Workload Migration
                        </Option>
                        <Option value="file" disabled={isMigrateNetAppFiles}>
                            File Migration
                        </Option>
                        <Option value="block_mobility">Block Migration</Option>
                    </Select>
                </Form.Item>
            )}

            {/* File Migration Type (conditional) */}
            {migrationType === 'file' && (
                <Form.Item
                    label={
                        <span>
                            Select File Migration Type{' '}
                            <Tooltip title="File Migration allows data to be seeded directly to a cloud disk. Cloud File Migration allows files to be migrated into cloud-native file storage.">
                                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                            </Tooltip>
                        </span>
                    }
                    name="fileMobilityType"
                    initialValue="dataOnlyMigration"
                >
                    <Select>
                        <Option value="dataOnlyMigration" disabled={isMigrateNetAppFiles}>
                            File Migration
                        </Option>
                        <Option value="netAppFilesMigration" disabled={!isMigrateNetAppFiles}>
                            Cloud Files Migration
                        </Option>
                    </Select>
                </Form.Item>
            )}

            {/* Block Migration Type (conditional) */}
            {migrationType === 'block_mobility' && (
                <Form.Item
                    label={
                        <span>
                            Select Block Migration Type{' '}
                            <Tooltip title="Existing Disk on Another Host - Used to clone data from a source host disk to a disk which is already attached to a target host. New Disk on Another Host - Used to clone data from a source host disk to a new disk which will be attached to a target host. New Disk Without Compute - Used to clone data from a source host disk to a new disk which will be left unattached.">
                                <InfoCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
                            </Tooltip>
                        </span>
                    }
                    name="blockMobilityType"
                    initialValue="existing_disk_on_host"
                >
                    <Select>
                        <Option value="existing_disk_on_host">Existing Disk on Another Host</Option>
                        <Option value="new_disk_on_host" disabled={!isNewDiskOnHostEnabled}>
                            New Disk on Another Host
                        </Option>
                        <Option value="new_cloud_disk" disabled={!isNewCloudDiskEnabled}>
                            New Disk Without Compute
                        </Option>
                        <Option value="unified" disabled={!isUnifiedEnabled}>
                            Disk Migration
                        </Option>
                    </Select>
                </Form.Item>
            )}
        </>
    );
};
